import { CommentOutlined } from "@material-ui/icons";
import HelpModal from "components/HelpModal";
import SubmitRMPAModal from "components/SubmitRMPAModal";
import { observer } from "mobx-react-lite";
import { collectSectionTabKeys } from "pages/CollectSection";
import { defineSectionTabKeys } from "pages/DefineSection";
import { outputSectionTabKeys } from "pages/OutputSection";
import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSaveEmit } from "../../pages/Workspace/hooks";
import { useStore } from "../../store";
import Project from "../../store/models/Project";
import { Toast } from "../../utils";
import ApproveModal from "../ApproveModal";
import Button from "../Button";
import PublishModal from "../PublishModal";
import RejectModal from "../RejectModal";
import SaveButton from "../SaveButton";
import SubmitModal from "../SubmitModal";

type Props = {
  urlPrefix: string;
  project: Project;
};

const NavbarButtons: React.FC<Props> = ({ urlPrefix, project }) => {
  const store = useStore();
  const location = useLocation();
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const { current: currentUser } = store.auth;

  const cltLocked =
    currentUser && project.lockedClusters.includes(currentUser.cluster);
  const cltOpen =
    currentUser && project.openClusters.includes(currentUser.cluster);
  const cltSubmitted =
    currentUser && project.submittedClusters.includes(currentUser.cluster);
  const cltApproved =
    currentUser && project.approvedClusters.includes(currentUser.cluster);
  const cltRejected =
    currentUser && project.rejectedClusters.includes(currentUser.cluster);

  const isDefineSection = location.pathname.endsWith(urlPrefix);
  const isCollectSection = location.pathname.startsWith(`${urlPrefix}/collect`);
  const isOutputSection = location.pathname.startsWith(`${urlPrefix}/analyze`);
  const isStatusSection = location.pathname.startsWith(`${urlPrefix}/status`);
  const isSettingsSection = location.pathname.startsWith(
    `${urlPrefix}/settings`
  );

  const isSubmitting = project.loading;

  const onSave = useSaveEmit(project);

  const handleUnsubmit = useCallback(async () => {
    try {
      await project.unsubmit();
    } catch (e) {
      Toast.danger("Unsubmit failed.");
      return;
    }
  }, [project]);

  const handleUnsubmitRMPA = useCallback(async () => {
    try {
      await project.unsubmitRMPA();
    } catch (e) {
      Toast.danger("Unsubmit failed.");
      return;
    }
  }, [project]);

  const handleSave = useCallback(async () => {
    try {
      await onSave();
      setIsSubmitSuccessful(true);
    } catch (e) {}
  }, [onSave]);

  const setNotSubmitSuccessful = () => {
    setIsSubmitSuccessful(false);
  };

  const handleCommentsClick = useCallback(() => {
    store.comments.toggleCommentsPanel();
  }, [store.comments]);

  if (!currentUser) {
    return null;
  }

  if (project.isLocked) {
    return null;
  }

  const isAdmin = store.auth.isProjectAdmin(project);

  if (isDefineSection) {
    const defineSubSection = (Object.keys(defineSectionTabKeys) as Array<keyof typeof defineSectionTabKeys>).find((item) => {
      return defineSectionTabKeys[item] === project.activeTab
    });
    const havePermission : boolean = defineSubSection ? store.auth.havePermissions(defineSubSection, 'define') : false;
    const showSave =
      havePermission && !cltLocked && (isAdmin || project.opsInputUsers.includes(currentUser.id) ||
        project.rmPriceUsers.includes(currentUser.id));

    const showPublish = store.auth.current?.rbacRole?.key === 'admin' && isAdmin;
    const showRMPA =
      project.rmPriceUsers.includes(currentUser.id) &&
      project.activeTab === "priceAssumptions";

    return (
      <>
        <HelpModal project={project} />
        {showSave ? (
          <SaveButton
            project={project}
            isSubmitting={isSubmitting}
            isSubmitSuccessful={isSubmitSuccessful}
            setNotSubmitSuccessful={setNotSubmitSuccessful}
            onClick={handleSave}
          />
        ) : null}
        {showPublish ? <PublishModal project={project} /> : null}
        {showRMPA ? (
          project.rmpaSubmitted ? (
            <Button onClick={handleUnsubmitRMPA}>Unsubmit</Button>
          ) : (
            <SubmitRMPAModal project={project} />
          )
        ) : null}
        {project.showComments ? (
          <Button onClick={handleCommentsClick}>
            <CommentOutlined /> Comments
          </Button>
        ) : null}
      </>
    );
  } else if (isCollectSection) {

    const collectSubSection = (Object.keys(collectSectionTabKeys) as Array<keyof typeof collectSectionTabKeys>).find((item) => {
      return collectSectionTabKeys[item] === project.activeTab
    });
    const havePermission : boolean = collectSubSection ? store.auth.havePermissions(collectSubSection, 'collect') : false;
    const showSave =
      havePermission && (isAdmin) && ((currentUser.cluster === project.activeCluster) && (cltOpen || cltRejected))

    console.log(currentUser.cluster, project.activeCluster, cltOpen, cltRejected);
    const showSubmit =
      havePermission && (currentUser.cluster === project.activeCluster) && (cltOpen || cltRejected);
    const showUnsubmit =
      havePermission && (currentUser.cluster === project.activeCluster) && cltSubmitted;
    const showApprove =
      havePermission && (currentUser.cluster === project.activeCluster) && cltSubmitted;
    const showUnapprove = havePermission && currentUser.cluster === project.activeCluster && cltApproved;
    const showReject =
      havePermission && !(currentUser.cluster === project.activeCluster) && (cltSubmitted || cltApproved);

    return (
      <>
        <HelpModal project={project} />
        {showSave ? (
          <SaveButton
            project={project}
            isSubmitting={isSubmitting}
            isSubmitSuccessful={isSubmitSuccessful}
            setNotSubmitSuccessful={setNotSubmitSuccessful}
            onClick={handleSave}
          />
        ) : null}
        {showSubmit ? <SubmitModal project={project} /> : null}
        {showUnsubmit ? (
          <Button onClick={handleUnsubmit}>Unsubmit</Button>
        ) : null}
        {showApprove ? <ApproveModal project={project} /> : null}
        {showUnapprove ? (
          <Button onClick={handleUnsubmit}>Unapprove</Button>
        ) : null}
        {showReject ? <RejectModal project={project} /> : null}
        {project.showComments ? (
          <Button onClick={handleCommentsClick}>
            <CommentOutlined /> Comments
          </Button>
        ) : null}
      </>
    );
  } else if (isOutputSection || isStatusSection || isSettingsSection) {

    let showSave = isAdmin;
    if (isOutputSection) {
      const outputSubSection = (Object.keys(outputSectionTabKeys) as Array<keyof typeof outputSectionTabKeys>).find((item) => {
        return outputSectionTabKeys[item] === project.activeTab
      });
      const havePermission : boolean = outputSubSection ? store.auth.havePermissions(outputSubSection, 'output') : false;
      showSave = havePermission && isAdmin;
    }
    return (
      <>
        <HelpModal project={project} />
        {showSave ? (
          <SaveButton
            project={project}
            isSubmitting={isSubmitting}
            isSubmitSuccessful={isSubmitSuccessful}
            setNotSubmitSuccessful={setNotSubmitSuccessful}
            onClick={handleSave}
          />
        ) : null}
        {project.showComments ? (
          <Button onClick={handleCommentsClick}>
            <CommentOutlined /> Comments
          </Button>
        ) : null}
      </>
    );
  }

  return null;
};

export default observer(NavbarButtons);
